import { Send } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import {
  Avatar,
  AvatarGroup,
  Box,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Grid,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import {
  updateUserRole,
  IWorkspace,
  inviteUser,
  IWorkspaceInvitationDTO,
  IInviteStatus,
  updatedUserMFA,
  checkIfUserDisabled,
  revokeAllAccess,
  deleteTeamMemberFromAllTeams,
  updateUserStatus,
  updateBusinessUnitById,
  IBusinessUnit,
} from 'wisestep-api';
import {
  ChatBoxHeader,
  WsButton,
  WsColors,
  WsDialog,
  WsTypography,
  WsTypoVariants,
  WsDrawer,
  WsTypoColors,
  WSIconTickMark,
  WsIconClockHour2,
  WsIconMinusCircle,
  WsIconExclamationTriangle,
  WsIconCross,
} from 'wisestep-base';

import { useSKUModalStore } from '../../../components/skuModal/SKUModalStore';
import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import { UserAccessAction, useUserAccessAuthz } from '../../../hooks/useUserAccessAuthz';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { stringAvatar, mapBusinessUnitIdsToNames } from '../helper/defaultValues';
import { IUserStatistics } from '../types/IUserStatistics';

import UserActionTable from './UserActionTable';

interface IStatusTypographyProps {
  cell: string;
}

const bgcolorStatusMap = new Map();

bgcolorStatusMap.set(IInviteStatus.INVITED, WsColors.Yellow100);
bgcolorStatusMap.set(IInviteStatus.ACTIVATED, WsColors.Green100);
bgcolorStatusMap.set(IInviteStatus.DEACTIVATED, WsColors.Red100);
bgcolorStatusMap.set('Invalid', WsColors.TertiaryShades100);
bgcolorStatusMap.set('Accepted', WsColors.Green100);
bgcolorStatusMap.set('Declined', WsColors.Red100);
bgcolorStatusMap.set('Cancelled', WsColors.Red100);

const colorStatusMap = new Map();

colorStatusMap.set(IInviteStatus.INVITED, WsColors.Yellow600);
colorStatusMap.set(IInviteStatus.ACTIVATED, WsColors.Green600);
colorStatusMap.set(IInviteStatus.DEACTIVATED, WsColors.Red500);
colorStatusMap.set('Invalid', WsColors.TertiaryShades800);
colorStatusMap.set('Accepted', WsColors.Green600);
colorStatusMap.set('Declined', WsColors.Red500);
colorStatusMap.set('Cancelled', WsColors.Red500);

const IconcolorStatusMap = new Map();

IconcolorStatusMap.set(
  IInviteStatus.INVITED,
  <WsIconClockHour2 color={colorStatusMap.get(IInviteStatus.INVITED)} />
);
IconcolorStatusMap.set(
  IInviteStatus.ACTIVATED,
  <WSIconTickMark color={colorStatusMap.get(IInviteStatus.ACTIVATED)} />
);
IconcolorStatusMap.set(
  IInviteStatus.DEACTIVATED,
  <WsIconMinusCircle color={colorStatusMap.get(IInviteStatus.DEACTIVATED)} />
);
IconcolorStatusMap.set(
  'Invalid',
  <WsIconExclamationTriangle color={colorStatusMap.get('Invalid')} />
);
IconcolorStatusMap.set(
  'Accepted',
  <WSIconTickMark color={colorStatusMap.get(IInviteStatus.ACTIVATED)} />
);
IconcolorStatusMap.set(
  'Declined',
  <WsIconCross color={colorStatusMap.get(IInviteStatus.DEACTIVATED)} />
);
IconcolorStatusMap.set(
  'Cancelled',
  <WsIconCross color={colorStatusMap.get(IInviteStatus.DEACTIVATED)} />
);

export const StatusTypography = styled(Typography)(({ cell }: IStatusTypographyProps) => ({
  backgroundColor: bgcolorStatusMap.get(cell),
  color: colorStatusMap.get(cell),
  padding: 4,
  width: 'fit-content',
  borderRadius: 4,
}));
type ChipIconType = {
  textColor: string;
  bgColor: string;
};

const StyledIconChip = styled(Chip)(({ textColor, bgColor }: ChipIconType) => ({
  color: textColor,
  background: bgColor,
  fontWeight: 700,
  paddingLeft: 10,
  paddingRight: 10,
  borderRadius: 4,
}));
export const StatusIconTypography = (params) => {
  return (
    <StyledIconChip
      label={params?.cell}
      icon={IconcolorStatusMap.get(params?.cell)}
      textColor={colorStatusMap.get(params?.cell)}
      bgColor={bgcolorStatusMap.get(params?.cell)}
    />
  );
};
export interface ITotalInvitesListProps {
  invitedUsers: IWorkspace[];
  actionCounts?: IUserStatistics | null;
  setInvitedUsers: Dispatch<SetStateAction<IWorkspace[]>>;
  roles: string[];
  maxUserLimit: number;
  pendingUsersLimit: number;
  setPendingUsersLimit: Dispatch<SetStateAction<number>>;
  refreshActionCount: () => void;
  refreshUsersCount: () => void;
  businessUnits: IBusinessUnit[];
}

const TotalInvitesList = ({
  invitedUsers,
  actionCounts,
  setInvitedUsers,
  roles,
  maxUserLimit,
  pendingUsersLimit,
  setPendingUsersLimit,
  refreshActionCount,
  refreshUsersCount,
  businessUnits,
}: ITotalInvitesListProps) => {
  const setModal = useSKUModalStore((state) => state.setModal);
  const [restrictStatusChange, setRestrictStatusChange] = useState(false);
  const workspace = useWorkspaceContext();
  const requiresMFA = workspace?.requiresMFA;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openActionDrawer, setOpenActionDrawer] = useState<boolean>(false);
  const [requiredConfirmation, setRequiredConfirmation] = useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [openBusinessUnit, setOpenBusinessUnit] = useState<boolean>(false);
  const [checkedBusinessUnit, setCheckedBusinessUnit] = useState<IBusinessUnit[]>([]);
  const [rowId, setRowId] = useState<number>(-1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAvatarIndex, setSelectedAvatarIndex] = useState(null);

  const handleOpenMenu = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedAvatarIndex(index);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedAvatarIndex(null);
  };

  const { access: userAccessControl } = useUserAccessAuthz([UserAccessAction.MANAGE_USERS_ACCESS]);

  const manageAccess = workspace.owns || userAccessControl[0];

  const onActionDrawerClose = () => {
    setOpenActionDrawer(false);
    setSelectedEmail('');
    setRequiredConfirmation(false);
  };

  const handleManageAccess = (email: string) => {
    setSelectedEmail(email);
    setOpenActionDrawer(true);
  };

  const handleTeamMemberRoleUpdate = useCallback(
    async (newRole: string, rowId: number) => {
      if (manageAccess) {
        const tempInvitedUsers = [...invitedUsers];
        tempInvitedUsers[rowId].role = newRole;
        setInvitedUsers([...tempInvitedUsers]);
        await updateUserRole(invitedUsers[rowId]);
      } else {
        setNotification(warning('You do not have enough permissions to perform this action'));
      }
    },
    [invitedUsers, manageAccess, setInvitedUsers, setNotification]
  );

  const handleBusinessUnitRoleUpdate = useCallback(async () => {
    setOpenBusinessUnit(false);
    if (manageAccess) {
      const tempInvitedUsers = [...invitedUsers];
      const updatedCheckedBusinessUnit = checkedBusinessUnit.map((unit) => ({
        ...unit,
        active: true,
      }));
      tempInvitedUsers[rowId].businessUnit = checkedBusinessUnit.map(
        (businessUnit) => businessUnit.id || ''
      );

      setInvitedUsers([...tempInvitedUsers]);
      await updateUserRole(invitedUsers[rowId]);
      await updateBusinessUnitById(
        updatedCheckedBusinessUnit,
        tempInvitedUsers[rowId]?.userId ?? '',
        tempInvitedUsers[rowId].id ?? ''
      );
    } else {
      setNotification(warning('You do not have enough permissions to perform this action'));
    }
  }, [checkedBusinessUnit, invitedUsers, manageAccess, rowId, setInvitedUsers, setNotification]);

  const handleCheckboxChange = (id: string, name: string) => {
    setCheckedBusinessUnit((prevCheckedBusinessUnit) => {
      const prevArray = Array.isArray(prevCheckedBusinessUnit) ? prevCheckedBusinessUnit : [];
      const isAlreadySelected = prevArray.some((item) => item.id === id);
      if (isAlreadySelected) {
        const updatedBusinessUnit = prevArray.filter((item) => item.id !== id);
        return updatedBusinessUnit;
      } else {
        const updatedBusinessUnit = [...prevArray, { id, name }];
        return updatedBusinessUnit;
      }
    });
  };

  const handleDownloadAccessChange = useCallback(
    async (rowId: number, checked: boolean) => {
      if (manageAccess) {
        const tempInvitedUsers = [...invitedUsers];
        tempInvitedUsers[rowId].roles = checked ? ['ROLE_USER', 'ROLE_DOWNLOAD'] : ['ROLE_USER'];
        setInvitedUsers([...tempInvitedUsers]);
        await updateUserRole(invitedUsers[rowId]);
      } else {
        setNotification(warning('You do not have enough permissions to perform this action'));
      }
    },
    [invitedUsers, manageAccess, setInvitedUsers, setNotification]
  );

  const validateUserAction = (email: string) => {
    setSelectedEmail(email);
    setRequiredConfirmation(true);
  };

  const revokeEmailFromTeamAndClientAndClientContact = async () => {
    try {
      if (selectedEmail) {
        const teamResponse = await deleteTeamMemberFromAllTeams(selectedEmail);
        const response = await revokeAllAccess(selectedEmail);
        if (teamResponse.status === 200 && response.status === 200) {
          setNotification(
            success('User unassigned from Team, Client and Client Contacts Successfully')
          );
          refreshActionCount();
          setRequiredConfirmation(false);
        }
      }
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const handleChangeStatus = useCallback(
    async (rowId: number) => {
      if (!manageAccess) {
        setNotification(warning('You do not have enough permissions to perform this action'));
        return;
      }
      const tempInvitedUsers = [...invitedUsers];
      if (!isEmpty(tempInvitedUsers) && workspace?.ownerId !== tempInvitedUsers[rowId]?.userId) {
        if (pendingUsersLimit > 0 || tempInvitedUsers[rowId].barred == false) {
          const response = await checkIfUserDisabled(
            invitedUsers[rowId]?.userId ?? '',
            invitedUsers[rowId]?.id ?? ''
          );
          const barredValue = isEmpty(response.data) ? !invitedUsers[rowId].barred : false;
          tempInvitedUsers[rowId].barred = barredValue ?? false;
          tempInvitedUsers[rowId].workspaceUser.barred = barredValue ?? false;
          await updateUserStatus(invitedUsers[rowId]?.workspaceUser);
          setInvitedUsers([...tempInvitedUsers]);
          if (tempInvitedUsers[rowId].barred == false) {
            setPendingUsersLimit(pendingUsersLimit - 1);
          } else {
            setPendingUsersLimit(pendingUsersLimit + 1);
          }
          refreshUsersCount();

          //  else {
          //   setNotification({
          //     type: 'error',
          //     message: 'This user has been disabled by WiseStep admin',
          //   });
          // }
        } else {
          setModal({
            object: {
              featureCode: '',
              type: '',
              countType: '',
              consumed: maxUserLimit - pendingUsersLimit,
              balance: pendingUsersLimit,
              actualLimit: maxUserLimit,
              message: '',
            },
            type: 'link',
          });
        }
      } else {
        setRestrictStatusChange(true);
      }
    },
    [
      invitedUsers,
      maxUserLimit,
      pendingUsersLimit,
      setInvitedUsers,
      setModal,
      setNotification,
      setPendingUsersLimit,
      workspace?.ownerId,
      manageAccess,
    ]
  );

  const closeRestrictionModal = () => setRestrictStatusChange(false);

  const resendInvite = useCallback(
    async (rowId: number) => {
      const payload: IWorkspaceInvitationDTO = {
        emails: [invitedUsers[rowId].userId || ''],
        role: invitedUsers[rowId].role,
        roles: invitedUsers[rowId].roles,
        workspaceId: invitedUsers[rowId].id,
      };
      await inviteUser(payload);
    },
    [invitedUsers]
  );

  const data = useMemo(
    () =>
      invitedUsers?.map((user) => ({
        email: user.userId,
        role: user.role,
        downloadAccess: user.roles?.includes('ROLE_DOWNLOAD'),
        enableMfa: user?.disableOTPValidation,
        businessUnit: mapBusinessUnitIdsToNames(user.businessUnit, businessUnits),
        status: user.barred
          ? IInviteStatus.DEACTIVATED
          : user.accepted
          ? IInviteStatus.ACTIVATED
          : IInviteStatus.INVITED,
      })),
    [businessUnits, invitedUsers]
  );

  const getClientCount = (prop: string, email?: string): string => {
    if (email) {
      return actionCounts
        ? ((actionCounts[prop] ? actionCounts[prop][email] : null) ?? 0) + ''
        : '0';
    } else return '0';
  };

  const getTotalCount = (email?: string): number => {
    if (email) {
      const totalCount =
        Number(getClientCount('teams', email)) +
        Number(getClientCount('client', email)) +
        Number(getClientCount('contacts', email));
      return totalCount;
    } else return 0;
  };

  const handleMFASetting = useCallback(
    async (rowId: number, checked: boolean) => {
      if (manageAccess) {
        await updatedUserMFA(invitedUsers[rowId].userId ?? '', !checked);
      } else {
        setNotification(warning('You do not have enough permissions to perform this action'));
      }
    },
    [invitedUsers, manageAccess, setNotification]
  );

  const onBusinessUnitChange = (index: number) => {
    setRowId(index);
    setCheckedBusinessUnit(data[index]?.businessUnit ?? []);
    setOpenBusinessUnit(true);
  };
  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ borderBottom: 'none' }}>Email</TableCell>
              <TableCell sx={{ borderBottom: 'none' }}>Role</TableCell>
              <TableCell sx={{ borderBottom: 'none' }}>Status</TableCell>
              {businessUnits && businessUnits?.length > 0 && (
                <TableCell sx={{ borderBottom: 'none' }}>Business Unit</TableCell>
              )}
              <TableCell sx={{ borderBottom: 'none' }}>Download Access</TableCell>
              {requiresMFA && <TableCell sx={{ borderBottom: 'none' }}>Enable MFA</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.map((item, index) => (
              <TableRow key={index} sx={{ border: 'none' }}>
                <TableCell sx={{ borderBottom: 'none' }} scope="row">
                  {item?.email}
                </TableCell>

                <TableCell sx={{ borderBottom: 'none' }}>
                  <Select
                    sx={{ minWidth: 200 }}
                    defaultValue={item?.role}
                    onChange={(ev) => handleTeamMemberRoleUpdate(ev.target.value, index)}
                    name="role"
                    label=""
                    disabled={!manageAccess}
                  >
                    {roles.map((role, i) => (
                      <MenuItem key={i} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>

                <TableCell sx={{ cursor: 'pointer', borderBottom: 'none' }}>
                  <Stack spacing={1} direction={'row'}>
                    <StatusTypography cell={item?.status} onClick={() => handleChangeStatus(index)}>
                      {item?.status}
                    </StatusTypography>
                    {item?.status === IInviteStatus.INVITED && (
                      <Tooltip title="Re-send">
                        <Icon>
                          <Send onClick={() => resendInvite(index)} />
                        </Icon>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>

                {businessUnits?.length > 0 && (
                  <TableCell sx={{ borderBottom: 'none' }}>
                    <Grid container spacing={1} direction="row" alignItems="center">
                      {workspace.owns && !(workspace.ownerId === item?.email) && (
                        <Grid item>
                          <Avatar
                            sx={{ width: 30, height: 30 }}
                            alt="add"
                            onClick={() => onBusinessUnitChange(index)}
                          >
                            <PersonAddAltIcon fontSize="small" />
                          </Avatar>
                        </Grid>
                      )}

                      {item?.businessUnit?.length > 0 ? (
                        <>
                          <Grid item>
                            <AvatarGroup
                              onMouseOver={(event) => handleOpenMenu(event, index)}
                              max={3}
                              componentsProps={{
                                additionalAvatar: {
                                  sx: {
                                    width: '30px',
                                    height: '30px',
                                    bgcolor: WsColors.TheBlues500,
                                    color: WsColors.White,
                                  },
                                },
                              }}
                            >
                              {item?.businessUnit?.map((option, index) => (
                                <Tooltip title={option} key={index}>
                                  <Avatar
                                    key={index}
                                    alt={option.name}
                                    {...stringAvatar(option.name ?? '', index)}
                                  />
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                          </Grid>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleCloseMenu}
                          >
                            {selectedAvatarIndex !== null &&
                              data[selectedAvatarIndex]?.businessUnit?.map((option, index) => (
                                <MenuItem key={index} sx={{ fontSize: '20px' }}>
                                  {option.name}
                                </MenuItem>
                              ))}
                          </Menu>
                        </>
                      ) : (
                        <Grid item>
                          <WsTypography variant={WsTypoVariants.BODY1}>No BU Added</WsTypography>
                        </Grid>
                      )}
                    </Grid>
                  </TableCell>
                )}

                <TableCell sx={{ borderBottom: 'none' }}>
                  <Switch
                    onChange={(ev) => handleDownloadAccessChange(index, ev.target.checked)}
                    defaultChecked={item?.downloadAccess}
                    disabled={!manageAccess}
                  />
                </TableCell>

                {requiresMFA && (
                  <TableCell sx={{ borderBottom: 'none' }}>
                    <Switch
                      onChange={(ev) => handleMFASetting(index, ev.target.checked)}
                      defaultChecked={!item?.enableMfa}
                      disabled={!manageAccess}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ borderBottom: 'none' }}>
                  <WsButton
                    startIcon={<InfoOutlinedIcon />}
                    variant="contained"
                    onClick={() => handleManageAccess(item?.email ?? '')}
                  >
                    Manage Access
                  </WsButton>
                </TableCell>
                {manageAccess && getTotalCount(item?.email ?? '') > 0 && (
                  <TableCell sx={{ borderBottom: 'none' }}>
                    <Tooltip title={'Reset Access'} arrow>
                      <Box
                        component={IconButton}
                        sx={{
                          width: '40px',
                          height: '30px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          backgroundColor: WsColors.TheBlues500,
                          ':hover': {
                            backgroundColor: WsColors.TheBlues600,
                          },
                        }}
                        onClick={() => validateUserAction(item?.email ?? '')}
                      >
                        <ReplayIcon sx={{ fontSize: 20, color: 'white' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {restrictStatusChange && (
        <WsDialog size="sm" openDialog={restrictStatusChange} onClose={closeRestrictionModal}>
          <ChatBoxHeader>
            <WsTypography variant={WsTypoVariants.SUB_TITLE} display="inline" bold={true}>
              Invite User
            </WsTypography>
            <CloseIcon
              sx={{ cursor: 'pointer', float: 'right', mt: 0.5 }}
              fontSize={'small'}
              onClick={closeRestrictionModal}
            />
          </ChatBoxHeader>
          <DialogContent>
            <Box>
              <WsTypography display="inline">
                Workspace admin cannot be deactivated. Please deactivate other user
              </WsTypography>
            </Box>
          </DialogContent>
          <DialogActions>
            <WsButton variant="outlined" onClick={closeRestrictionModal}>
              Close
            </WsButton>
          </DialogActions>
        </WsDialog>
      )}
      {openActionDrawer && (
        <WsDrawer
          open={openActionDrawer}
          onClose={onActionDrawerClose}
          title={'Manage Access'}
          headerSize="medium"
          size="medium"
        >
          <UserActionTable
            email={selectedEmail}
            closeDrawer={onActionDrawerClose}
            refeshCount={refreshActionCount}
          />
        </WsDrawer>
      )}
      <WsDialog size="medium" openDialog={requiredConfirmation}>
        <DialogContent
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <ReplayCircleFilledIcon
            sx={{ height: 75, width: 75, color: WsColors.TheBlues600, mt: 1 }}
          />
          <Box sx={{ padding: '50px' }}>
            <WsTypography variant={WsTypoVariants.TITLE}>
              {
                "Clicking on 'Yes' will reset all the access provided to the user for Teams and Clients.User will still be able to access the workspace."
              }
            </WsTypography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignSelf: 'flex-end',
              paddingRight: '50px',
              mt: 1,
            }}
          >
            <WsButton
              onClick={() => onActionDrawerClose()}
              type="button"
              size="large"
              variant="outlined"
            >
              No
            </WsButton>
            <Box p={1}></Box>
            <WsButton
              type="submit"
              size="large"
              onClick={() => revokeEmailFromTeamAndClientAndClientContact()}
            >
              Yes
            </WsButton>
          </Box>
        </DialogContent>
      </WsDialog>

      <WsDialog size="small" openDialog={openBusinessUnit}>
        <DialogContent style={{ overflowX: 'hidden' }}>
          <Grid container justifyContent="center" direction="column" alignItems="center">
            <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK} bold>
              Select a Business Unit
            </WsTypography>
            <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
              Select a Business Unit which you want to tag to the User
            </WsTypography>
          </Grid>
          <Grid container justifyContent="flex-start" ml={4} pt={1} pb={2}>
            <FormGroup>
              {businessUnits.map((item) => (
                <FormControlLabel
                  key={item.id}
                  control={
                    <Checkbox
                      defaultChecked={
                        Array.isArray(data[rowId]?.businessUnit)
                          ? data[rowId]?.businessUnit?.some((unit) => unit.id === (item.id ?? ''))
                          : false
                      }
                      onChange={() => handleCheckboxChange(item.id ?? '', item.name ?? '')}
                    />
                  }
                  label={
                    <WsTypography
                      variant={WsTypoVariants.SUB_TITLE}
                      color={WsTypoColors.PRIMARY}
                      cursor="pointer"
                    >
                      {item.name}
                    </WsTypography>
                  }
                />
              ))}
            </FormGroup>
          </Grid>
          <Grid container justifyContent="space-between">
            <Grid item>
              <WsButton
                onClick={() => setOpenBusinessUnit(false)}
                type="button"
                size="medium"
                variant="outlined"
              >
                Cancel
              </WsButton>
            </Grid>
            <Grid item>
              <WsButton onClick={handleBusinessUnitRoleUpdate} type="button" size="medium">
                Save
              </WsButton>
            </Grid>
          </Grid>
        </DialogContent>
      </WsDialog>
    </>
  );
};

export default TotalInvitesList;
